import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { faBan, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { forkJoin } from 'rxjs';
import { ErrorService } from '../../../../@core/utils/error.service';
import { KmlService } from '../../../../@core/utils/kml.service';
import { SessionService } from '../../../../@core/utils/session.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { Fieldman } from '../../models/fieldman';
import { FieldmanService } from '../../services/Fieldman.service';
import { FieldmanEditListComponent } from './fieldman-edit-list/fieldman-edit-list.component';
import { FieldmanListDataSource } from './fieldman-list-datasource';

@Component({
  selector: 'fieldman-list',
  templateUrl: './fieldman-list.component.html',
  styleUrls: ['./fieldman-list.component.css']
})
export class FieldmanListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  //#region Properties
  dataSource: FieldmanListDataSource;
  public editfieldman: Fieldman = {};
  public fieldmanId: number;
  public fieldmanLoading = false;
  public submitting: any;
  public editActive = false;
  public allFieldMan: Fieldman[] = [];
  public savingFieldmanIds: Set<number> = new Set<number>();
  public highlightedFieldmanId: number = null;
  private pendingScrollToFieldmanId: number = null;
  public allColumns: ColumnModel[] = [];
  public gridName: string = "FieldmanConfigGrid";
  public pageSettings: PageSettingsModel = {
    pageSize: 50,
    pageSizes: [50, 100, 200],
  };
  public filterSettings: FilterSettingsModel = {
    mode: "Immediate",
    type: "Excel",
  };
  public toolbarOptions: string[] = [
    "ExcelExport",
  ];
  public enablePersistence: boolean = true;
  public filterCount: number = 0;
  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  displayedColumns = ['firstName', 'lastName', 'email', 'phone', 'edit'];
  //#endregion

  //#region Icon Properties
  public faPlus = faPlus;
  public faPen = faPen;
  public faTrash = faTrash;
  public faBan = faBan;
  //#endregion

  //#region ViewChild Properties
  @ViewChild(MatPaginator, { static: false }) 
  paginator: MatPaginator;

  @ViewChild(MatSort, { static: false }) 
  sort: MatSort;

  @ViewChild("grid", { static: true }) 
  grid: GridComponent;

  @ViewChild("actionTemplate", { static: true })
  public actionTemplate: any;

  @ViewChild("isActiveTemplate", { static: false })
  public isActiveTemplate: any;
  //#endregion

  //#region Constructor with dependencies
  constructor(
    private fieldmanService: FieldmanService,
    private errorService: ErrorService,
    private kmlService: KmlService,
    private toastrService: NbToastrService,
    private sessionService: SessionService,
    public dialogService: MatDialog
  ) { }
  //#endregion
  
  ngOnInit() {
    this.dataSource = new FieldmanListDataSource(this.paginator, this.sort);
    this.fieldmanLoading = true;

    this.fieldmanService.getFieldmen().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:data => {
      this.dataSource.data = data;
      if(this.paginator) this.paginator._changePageSize(this.paginator.pageSize);
      this.fieldmanLoading = false;
    }, error:err => { this.errorService.handleError(err); this.fieldmanLoading = false; }});
    this.loadData();
  }

  loadData() {
    this.fieldmanLoading = true;

    forkJoin([
      this.fieldmanService.getFieldmen(),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:
      (results) => {
        this.allFieldMan = results[0];

        this.fieldmanLoading = false;
        this.setColumns();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.fieldmanLoading = false;
      }}
    );
  }

  toolbarClick(args) {
    this.fieldmanLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.item.properties.text == "Excel Export") {
      this.grid
        .excelExport({
          fileName: `FieldmanExport.xlsx`,
        })
        .then(() => {
          this.fieldmanLoading = false;
        });
    }
  }

  setColumns() {
    this.allColumns = [];

    // Hidden primary key column - lets us look up/scroll to a specific row
    // (e.g. to scroll to and highlight a newly created fieldman).
    this.allColumns.push({
      field: "id",
      isPrimaryKey: true,
      visible: false,
    });

    this.allColumns.push(
      ...[
        {
          field: "firstName",
          headerText: "FirstName",
        },
        {
          field: "lastName",
          headerText: "LastName",
        },
        {
          field: "email",
          headerText: "Email",
        },
        {
          field: "phone",
          headerText: "Phone",
        },
        {
          headerText: "Action",
          template: this.actionTemplate,
          width: 200,
        },
      ]
    );
  }
  
  confirmDelete(id: number) {
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this field man?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "400px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        this.deleteRec(id);
      }
    });
  }

  deleteRec(id: number) {
    this.fieldmanLoading = true;
    this.fieldmanService.deleteFieldman(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:() => {
        this.toastrService.info("Field man successfully deleted!");
        this.fieldmanLoading = false;
        this.loadData();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.fieldmanLoading = false;
      }}
    );
  }

  openDialog(IsEdit: boolean, data?: Fieldman) {
    const fieldManDetailsDialog = this.dialogService.open(FieldmanEditListComponent, {
      width: "500px",
    });
    if (IsEdit) {
      var persistData = (this.grid as GridComponent).getPersistData();
      window.localStorage.setItem("grid" + this.gridName, persistData);
      // Clone: never hand the dialog a live reference into allFieldMan, or its
      // two-way-bound form fields would mutate the grid row before Save.
      this.sessionService.selectedFieldMan = { ...data };
    } else {
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedFieldMan = {} as Fieldman;
    }

    fieldManDetailsDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.backgroundSave && result.fieldman) {
        if (result.isNew) {
          this.createFieldmanInBackground(result.fieldman);
        } else {
          this.saveFieldmanInBackground(result.fieldman);
        }
      } else if (result) {
        this.loadData();
      }
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedFieldMan = {} as Fieldman;
    });
  }

  private createFieldmanInBackground(fieldman: Fieldman): void {
    // The fieldman doesn't exist server-side yet, so show a temporary
    // placeholder row (negative id so it can't collide with a real one)
    // with the saving spinner until the server assigns a real id.
    const tempId = -Date.now();
    this.savingFieldmanIds.add(tempId);
    this.insertOrReplaceFieldmanRow({ ...fieldman, id: tempId });

    this.fieldmanService.postFieldman(fieldman).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        if (!created || created.id == null) {
          // No usable id back from the API - can't show a real row, so
          // treat it the same as a failure instead of leaving a stuck
          // temp row behind.
          this.removeFieldmanRow(tempId);
          this.savingFieldmanIds.delete(tempId);
          this.errorService.handleError(new Error('Create Fieldman did not return a valid id'));
          this.toastrService.danger(
            'Failed to create Fieldman "' + this.fieldmanDisplayName(fieldman) + '". Please try again.',
            "Create failed"
          );
          return;
        }
        this.removeFieldmanRow(tempId);
        this.savingFieldmanIds.delete(tempId);
        this.insertOrReplaceFieldmanRow(created);
        this.jumpToFieldmanRow(created.id);
        this.toastrService.success(
          "Fieldman successfully created!",
          this.fieldmanDisplayName(fieldman) + " created!"
        );
      },
      error: (err) => {
        this.removeFieldmanRow(tempId);
        this.savingFieldmanIds.delete(tempId);
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to create Fieldman "' + this.fieldmanDisplayName(fieldman) + '". Please try again.',
          "Create failed"
        );
      }
    });
  }

  private saveFieldmanInBackground(fieldman: Fieldman): void {
    const originalFieldman = this.allFieldMan.find((f) => f.id === fieldman.id);
    this.savingFieldmanIds.add(fieldman.id);
    this.insertOrReplaceFieldmanRow(fieldman);
    this.refreshGridSoon();

    this.fieldmanService.putFieldman(fieldman).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        this.savingFieldmanIds.delete(fieldman.id);
        // 204 No Content leaves `updated` null/undefined - fall back to the fieldman we sent.
        this.insertOrReplaceFieldmanRow(updated || fieldman);
        this.refreshGridSoon();
        this.toastrService.success(
          "Fieldman details successfully updated!",
          this.fieldmanDisplayName(fieldman) + " updated!"
        );
      },
      error: (err) => {
        this.savingFieldmanIds.delete(fieldman.id);
        if (originalFieldman) {
          this.insertOrReplaceFieldmanRow(originalFieldman);
        }
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to update Fieldman "' + this.fieldmanDisplayName(fieldman) + '". Please try again.',
          "Save failed"
        );
      }
    });
  }

  private fieldmanDisplayName(fieldman: Fieldman): string {
    return [fieldman.firstName, fieldman.lastName]
      .filter(part => !!part && part.trim().length > 0)
      .join(' ')
      .trim() || 'Fieldman';
  }

  private insertOrReplaceFieldmanRow(fieldman: Fieldman): void {
    const index = this.allFieldMan.findIndex((f) => f.id === fieldman.id);
    const next = this.allFieldMan.slice();
    if (index !== -1) {
      next[index] = fieldman;
    } else {
      next.push(fieldman);
    }
    this.allFieldMan = next;
    this.refreshGridSoon();
  }

  private removeFieldmanRow(id: number): void {
    this.allFieldMan = this.allFieldMan.filter((f) => f.id !== id);
    this.refreshGridSoon();
  }

  // Scrolling happens once the grid actually finishes its next render (see
  // onGridDataBound) rather than after a guessed delay - refresh()/
  // goToPage() both fire dataBound when they're truly done, so this can't
  // fire too early against stale row data the way a fixed setTimeout can.
  private jumpToFieldmanRow(id: number): void {
    this.highlightedFieldmanId = id;
    this.pendingScrollToFieldmanId = id;

    if (!this.grid || !this.isDefaultSortActive()) {
      // Can't reliably compute which page the row lands on under a custom
      // sort - it'll still get highlighted/scrolled to whenever it's next
      // rendered on whatever page the user navigates to.
      return;
    }

    // Defer past the refresh(es) already queued by insertOrReplaceFieldmanRow/
    // removeFieldmanRow so this pages using the grid's up-to-date data.
    setTimeout(() => {
      const index = this.allFieldMan.findIndex((f) => f.id === id);
      if (index === -1) {
        return;
      }

      const pageSize = this.pageSettings.pageSize || this.allFieldMan.length;
      const page = Math.floor(index / pageSize) + 1;

      if (this.grid.pageSettings.currentPage !== page) {
        this.grid.goToPage(page);
      }
      // Already on the right page - the refresh queued by
      // insertOrReplaceFieldmanRow will trigger dataBound shortly on its own.
    });
  }

  // Applies the green highlight directly (and times its removal) right when
  // the row is actually visible, instead of leaving rowDataBound to start
  // the clock the first time it happens to touch this row - which could be
  // well before the row is scrolled into view.
  private startHighlightFade(row: Element, id: number): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    rowElement.classList.add("row-highlight-new");
    if (this.highlightedFieldmanId === id) {
      this.highlightedFieldmanId = null;
    }
    setTimeout(() => {
      rowElement.classList.remove("row-highlight-new");
    }, 2000);
  }

  // Scrolls the grid's own internal content viewport, not the page - a
  // plain row.scrollIntoView() walks every scrollable ancestor (including
  // the page), which in practice was moving the page while leaving the
  // grid's own internal scroll area untouched, so the row stayed hidden.
  private scrollGridRowIntoView(row: Element): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    const scrollableContent = this.grid.getContent().querySelector(".e-content") as HTMLElement;
    if (scrollableContent) {
      const targetScrollTop = rowElement.offsetTop
        - (scrollableContent.clientHeight / 2)
        + (rowElement.offsetHeight / 2);
      scrollableContent.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: "auto" });
    } else if (typeof rowElement.scrollIntoView === "function") {
      rowElement.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }

  private isDefaultSortActive(): boolean {
    // Fieldman has no baked-in default sort column (unlike Blocks, which
    // defaults to sorting by name) - "default" here just means the user
    // hasn't applied a custom sort, so paging math against allFieldMan's
    // natural order still lines up with what the grid shows.
    const cols = (this.grid && this.grid.sortSettings && this.grid.sortSettings.columns) || [];
    return cols.length === 0;
  }

  // Defers the grid refresh to the next macrotask so Angular has already
  // pushed the updated allFieldMan/savingFieldmanIds state into the grid's
  // own [dataSource] binding before we force it to re-render.
  private refreshGridSoon(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.refresh();
      }
    });
  }

  onRowDataBound(args: any): void {
    if (args.data && this.savingFieldmanIds.has(args.data.id)) {
      args.row.classList.add('row-saving');
    } else if (args.row) {
      args.row.classList.remove('row-saving');
    }
    if (args.data && this.highlightedFieldmanId != null && args.data.id === this.highlightedFieldmanId) {
      // Just a reactive preview if this row happens to render again before
      // the row is actually scrolled into view - startHighlightFade (called
      // once onGridDataBound confirms the scroll) owns clearing
      // highlightedFieldmanId and timing the class's removal.
      args.row.classList.add('row-highlight-new');
    }
  }

  onActionComplete(args: any) {
    if (args.requestType === 'filtering') {
      this.updateFilterCount();
    }
  }

  onGridDataBound() {
    this.updateFilterCount();

    if (this.pendingScrollToFieldmanId != null) {
      // goToPage() can fire this from a render that still has stale data
      // (queued just ahead of the refresh that actually adds the row), so
      // only clear the pending id once we've actually found and scrolled to
      // it - otherwise keep waiting for the next dataBound to try again.
      const id = this.pendingScrollToFieldmanId;
      const rowIndex = this.grid.getRowIndexByPrimaryKey(id);
      if (rowIndex > -1) {
        this.scrollGridRowIntoView(this.grid.getRowByIndex(rowIndex));
        this.pendingScrollToFieldmanId = null;
        this.startHighlightFade(this.grid.getRowByIndex(rowIndex), id);

        // Something (sticky header repositioning after the scroll, etc.)
        // can occasionally still leave the position half a beat behind on
        // the very first attempt - nudge it again shortly after so it
        // reads as part of the same jump rather than a second, separate
        // scrolling motion.
        setTimeout(() => {
          const idx = this.grid.getRowIndexByPrimaryKey(id);
          if (idx > -1) {
            const row = this.grid.getRowByIndex(idx);
            this.scrollGridRowIntoView(row);
            this.startHighlightFade(row, id);
          }
        }, 200);
      }
    }
  }

  private updateFilterCount() {
    const cols = (this.grid && this.grid.filterSettings && this.grid.filterSettings.columns) ? this.grid.filterSettings.columns : [];
    const uniqueFields = cols.map(c => c.field as string).filter((f: string, i: number, a: string[]) => a.indexOf(f) === i);
    this.filterCount = uniqueFields.length;
  }

  clearFilters() {
    window.localStorage.setItem("gridFieldmanConfigFilters", ""); // Here grid is component name and OrderDetails is component ID
    this.grid.clearFiltering();
    this.filterCount = 0;
  }
  downloadFiles(args){
    this.fieldmanLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.includes("Excel")) {
      this.grid
        .excelExport({
          fileName: `FieldmanExport.xlsx`,
        })
        .then(() => {
          this.fieldmanLoading = false;
        });
    }
  }
}
